import classNames from 'classnames';
import { memo, useCallback, useState } from 'react';

import { PlayIcon, XIcon } from '@air/design-icons';
import {
  FileStatusButton,
  FileStatusPanelItem,
  FileStatusSubtitle,
  FileStatusTitle,
} from '@air/feature-file-status/ui';

import { SHARED_PANEL_ITEM_CLASS_NAMES } from '../../constants/ClassNames';
import { getSubtitleWithProgress } from '../../utils';
import { FileStatusThumbnail } from './FileStatusThumbnail';
import { type FileTrackingItem } from './FileStatusTrackingPanel';

export type FileStatusPanelListItemCancelledProps = {
  item: FileTrackingItem;
  onCancel?: (item: FileTrackingItem) => void;
  onResume?: (item: FileTrackingItem) => Promise<void> | void;
};

export const FileStatusTrackingPanelListItemStopped = memo(
  ({
    item,
    onCancel,
    onResume,
    item: { ext, onRowClick, previewLoader, avatar, title, thumbnailSource, sizeInBytes, subtitle, status },
  }: FileStatusPanelListItemCancelledProps) => {
    const [isResuming, setIsResuming] = useState(false);

    const handleResume = useCallback(async () => {
      setIsResuming(true);
      try {
        await onResume?.(item);
      } finally {
        setIsResuming(false);
      }
    }, [onResume, item]);

    return (
      <FileStatusPanelItem
        avatar={avatar ?? <FileStatusThumbnail ext={ext} status={status} thumbnailSource={thumbnailSource} />}
        buttons={
          <>
            {onResume && (
              <FileStatusButton appearance="ghost" isLoading={isResuming} label="Resume" onClick={handleResume}>
                <PlayIcon size="sm" />
              </FileStatusButton>
            )}
            {onCancel && (
              // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
              <FileStatusButton appearance="ghost" label="Cancel" onClick={() => onCancel(item)}>
                <XIcon size="sm" />
              </FileStatusButton>
            )}
          </>
        }
        className={classNames(SHARED_PANEL_ITEM_CLASS_NAMES, !!onRowClick && 'cursor-pointer hover:bg-gray-3')}
        onClick={onRowClick}
        previewLoader={previewLoader}
        subtitle={
          <FileStatusSubtitle className="truncate">
            {subtitle ?? getSubtitleWithProgress({ sizeInBytes, title: 'Stopped', uploadedBytes: sizeInBytes })}
          </FileStatusSubtitle>
        }
        title={<FileStatusTitle className="text-12">{title}</FileStatusTitle>}
      />
    );
  },
);

FileStatusTrackingPanelListItemStopped.displayName = 'FileStatusTrackingPanelListItemStopped';
